import { NodeApi } from '@candulabs/craft';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { CRAFT_ROOT_ID } from 'craft/constants';
import { useTraversal } from 'craft/hooks/useSelection/useTraversal';
import { ValidationError } from 'craft/types';

import { getMaxNth, nodeLayerScale } from '../utils';

export interface ContextValue {
  /**
   *
   * exposing the node errors
   */
  errorNodes: Record<string, ValidationError>;
  /**
   *
   * exposing the nodeApi
   */
  getNode?: (nodeId: string) => NodeApi;

  /**
   * toggling at global level the global nodes wrapped / expanded
   */
  updateExpandedState?: (nodeId: string, expanded: boolean) => void;

  /**
   * scale of the node layer
   */
  scale: 'normal' | 'small' | 'xsmall';
}

export const EditorContext = React.createContext<ContextValue>({
  errorNodes: {},
  scale: 'normal',
});

export type EditorNodeDataProps = {
  errorNodes: ValidationError[];
  getNode: (nodeId: string) => NodeApi;
  children: React.ReactNode;
};

export const EditorNodeData: React.FC<EditorNodeDataProps> = ({
  errorNodes: errorNodeArray,
  getNode,
  children,
}: EditorNodeDataProps) => {
  const traversal = useTraversal();
  const initialExpanded = useRef(
    Object.fromEntries(
      traversal
        .descendants(CRAFT_ROOT_ID)
        .map((nodeId) => [
          nodeId,
          getNode(nodeId).isCanvas() && getNode(nodeId).childNodes().length > 0,
        ]),
    ),
  );

  const [scale, setScale] = useState<'normal' | 'small' | 'xsmall'>(
    nodeLayerScale(getMaxNth(CRAFT_ROOT_ID, initialExpanded.current, getNode, 0)),
  );

  const updateExpandedState = useCallback(
    (nodeId: string, isExpanded: boolean) => {
      initialExpanded.current[nodeId] = isExpanded;

      const nthLayerDisplayed = getMaxNth(CRAFT_ROOT_ID, initialExpanded.current, getNode, 0);
      setScale(nodeLayerScale(nthLayerDisplayed));
    },
    [getNode],
  );

  const errorNodes = useMemo(() => {
    return Object.fromEntries(errorNodeArray.map((errorEntry) => [errorEntry.nodeId, errorEntry]));
  }, [errorNodeArray]);

  return (
    <EditorContext.Provider value={{ errorNodes, scale, getNode, updateExpandedState }}>
      {children}
    </EditorContext.Provider>
  );
};
